/**
 * What's new — release notes shown to customers in the app.
 *
 * Add the newest release at the TOP. The id must be unique and increase
 * (date-based, YYYY-MM-DD). Each user sees a dot on "What's new" until
 * they've opened the page after the latest release.
 *
 * Writing guide (shop owners and cashiers read this, not developers):
 *   - kind:  "new" (a feature they didn't have), "improved" (works better),
 *            "fixed" (something that was wrong is now right).
 *   - area:  where in Eaze it lives — used for the filter chips.
 *   - title: what it is, in a few words. body: why it helps, how to use it.
 *   - link:  an in-app route to try it ("Try it" button). Optional.
 */
export type ReleaseKind = "new" | "improved" | "fixed";
export type ReleaseArea = "Till" | "Stock" | "Receipts" | "Billing" | "Settings" | "Everywhere";

export interface ReleaseItem {
  kind: ReleaseKind;
  area: ReleaseArea;
  title: string;
  body: string;
  where?: string;
  link?: string;
}

export interface ReleaseNote {
  id: string; // "2026-10-06"
  date: string; // display date
  title: string;
  summary?: string;
  audience?: "everyone" | "owners" | "cashiers";
  items: ReleaseItem[];
}

export const RELEASES: ReleaseNote[] = [
  {
    id: "2026-10-12-10",
    date: "12 October 2026",
    title: "Accounts an accountant will recognise",
    summary: "Profit & loss with gross profit, a balance sheet, closing the books, and Ghana's 2026 VAT rules.",
    audience: "owners",
    items: [
      {
        kind: "improved",
        area: "Everywhere",
        title: "Profit & loss, laid out properly",
        body: "Sales, less returns and discounts, then cost of sales and gross profit (with your margin), operating expenses and net profit. Discounts and promotions now show as their own line instead of quietly lowering sales.",
        link: "/reports/statements?tab=pnl",
      },
      {
        kind: "new",
        area: "Everywhere",
        title: "Balance sheet",
        body: "What the business owns, owes and is worth on any date, including this year's profit — and a check that it balances.",
        link: "/reports/statements?tab=bs",
      },
      {
        kind: "new",
        area: "Settings",
        title: "Close the books",
        body: "After checking a month, close it so nothing dated in it can change. Corrections go into the current month. Settings → Business → Close the books.",
        link: "/settings",
      },
      {
        kind: "improved",
        area: "Everywhere",
        title: "Ghana VAT from 1 January 2026",
        body: "For VAT-registered businesses in Ghana: VAT 15% + NHIL 2.5% + GETFund 2.5% on the same price (20%), all claimable on purchases; the COVID-19 levy is gone. Applies to new sales and expenses.",
      },
      {
        kind: "fixed",
        area: "Everywhere",
        title: "Gross profit means gross profit",
        body: "The profit card on Reports always shows gross profit (sales less cost of sales), with net profit as its own card — before, the same card changed meaning when you filtered by cashier.",
      },
    ],
  },
  {
    id: "2026-10-12-9",
    date: "12 October 2026",
    title: "Your currency, a tidier menu, and Report Center",
    summary: "Eaze now works in your country's currency, the menu shows only what your business and your role use, and every report is in one place.",
    audience: "everyone",
    items: [
      {
        kind: "improved",
        area: "Everywhere",
        title: "Your currency everywhere",
        body: "Prices, receipts, reports and the till use your business's currency — naira, shillings, rand, CFA francs and more — not cedis. New sign-ups choose their country. Businesses in Ghana see no change.",
      },
      {
        kind: "improved",
        area: "Everywhere",
        title: "A menu that fits your business",
        body: "The menu is grouped (Sell, Stock, Money, Insights…) and shows only what your kind of business uses and what your role allows. A salon no longer sees Manufacturing; a team member only sees what they can open.",
      },
      {
        kind: "new",
        area: "Everywhere",
        title: "Report Center",
        body: "Every report in one place: overview and trends, profit & loss, cash flow, trial balance, VAT, receivables, stock and controls. Statements come straight from your books and can be printed.",
        where: "Menu → Insights → Report Center",
        link: "/reports",
      },
      {
        kind: "fixed",
        area: "Everywhere",
        title: "No more blank screens",
        body: "If a screen hits a problem it now says so with a Try again button, and after an update it asks you to reload instead of going blank.",
      },
    ],
  },
  {
    id: "2026-10-12-8",
    date: "12 October 2026",
    title: "Keep selling when the internet drops",
    summary: "Cash sales carry on without internet and are sent by themselves when the connection is back — never twice.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Till",
        title: "Offline cash sales",
        body: "When the internet goes, the till says so at the bottom and keeps selling for cash. Each sale is saved on that till, stock goes down straight away, and scanning still works. When the connection is back the sales are sent automatically, dated when they were made. Mobile money and part payments need internet.",
        where: "Till → bottom bar",
      },
      {
        kind: "new",
        area: "Till",
        title: "Sales that need a look",
        body: "If a saved sale can't be recorded — for example the system shows too little stock — the bar says \"needs attention\". Tap it to see why, fix it, and try again. Shifts can't be closed while their offline sales are still on the till, so the drawer always adds up.",
      },
      {
        kind: "improved",
        area: "Till",
        title: "No double sales on a bad connection",
        body: "A sale that's sent twice — a dropped connection, a second tab — is recorded once, however late the second copy arrives. Offline sales show \"Made offline\" on the sale, with anything a manager should check.",
      },
    ],
  },
  {
    id: "2026-10-12-7",
    date: "12 October 2026",
    title: "Stock take",
    summary: "Count your shelves with your team, see exactly what's missing or extra, and fix stock and books in one step.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Stock",
        title: "Count with your team",
        body: "Start a count for everything or one category. Anyone can help: scan each item (a carton adds the whole carton, a scale label its weight) or type the total. Keep selling while you count — each item is checked against stock at the moment it's counted.",
        where: "Sidebar → Stock take · Cashier menu → Stock count",
        link: "/stock-take",
      },
      {
        kind: "new",
        area: "Stock",
        title: "See what's different, then post it",
        body: "Managers see every difference and what it's worth. Post corrections and stock is fixed and your books record the gain or loss. Counters don't see the expected numbers, so counts stay honest.",
        link: "/stock-take",
      },
    ],
  },
  {
    id: "2026-10-12-6",
    date: "12 October 2026",
    title: "My shift — reconcile your drawer",
    summary: "Cashiers get their own shift page: what should be in the drawer, mobile money to check against the phone, and how past shifts closed.",
    audience: "cashiers",
    items: [
      {
        kind: "new",
        area: "Till",
        title: "My shift",
        body: "See your open shift at a glance: sales, cash that should be in the drawer (and exactly how it adds up), and mobile money taken to check against your MoMo phone. Open the cash drawer or close your shift from there.",
        where: "Cashier menu → My shift",
        link: "/cashier/shifts",
      },
      {
        kind: "new",
        area: "Till",
        title: "Past shifts",
        body: "Every closed shift with what you counted, what was expected and whether you were exact, short or over — with the Z-report. (If your business uses blind close, your manager sees the differences instead.)",
        link: "/cashier/shifts",
      },
    ],
  },
  {
    id: "2026-10-12-5",
    date: "12 October 2026",
    title: "Run the cash drawer properly",
    summary: "Drop cash to the safe, pay small expenses from the till, add change — and close with a count that has to add up.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Till",
        title: "Cash drawer",
        body: "Tap Cash drawer at the till to record a drop (cash taken to the safe or the bank), a payout (petty cash spent — a manager types their PIN and it's recorded as an expense) or a pay in (more change). The drawer shows what should be in it right now.",
        where: "Till → Cash drawer",
      },
      {
        kind: "new",
        area: "Till",
        title: "Count notes and coins",
        body: "When you close your shift, tap \"Count notes and coins\" and type how many of each — the total adds itself up.",
        where: "Close shift",
      },
      {
        kind: "new",
        area: "Settings",
        title: "Blind close",
        body: "Turn on blind close and cashiers count the drawer without seeing how much should be in it. Only managers see the expected cash and any shortage.",
        where: "Settings → Business → Closing the till",
        link: "/settings",
      },
      {
        kind: "improved",
        area: "Everywhere",
        title: "Expected cash includes everything",
        body: "Expected cash at close now takes off drops and payouts and adds pay-ins, and the Z-report lists each. Banked cash moves to your bank account in the books; payouts show in Expenses.",
      },
    ],
  },
  {
    id: "2026-10-12-4",
    date: "12 October 2026",
    title: "Promotions that run themselves",
    summary: "3 for GH₵10, buy 2 get 1 free, 20% off, a special price — set it once and the till applies it.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Till",
        title: "Promotions",
        body: "Create an offer on chosen products with a start and end date. The till applies it automatically, shows the saving on the cart line and the receipt, and always gives the customer the best offer for each product.",
        where: "Sidebar → Promotions",
        link: "/promotions",
      },
      {
        kind: "new",
        area: "Everywhere",
        title: "See how each promotion did",
        body: "Each promotion shows how many sales used it and how much customers saved. Returns of promotion items refund what the customer actually paid for them.",
        link: "/promotions",
      },
    ],
  },
  {
    id: "2026-10-12-3",
    date: "12 October 2026",
    title: "Sell by the kilo",
    summary: "Rice, meat, tomatoes: set a price per kg, weigh at the till, and stock and profit stay exact to the gram.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Stock",
        title: "Products sold by weight",
        body: "Tick \"Sold by weight\" when you add a product (or switch it on under Inventory while its stock is 0). Prices and cost are per kg; stock is counted to the gram.",
        where: "Products → Add product → Stock · or open a product → Inventory",
        link: "/products",
      },
      {
        kind: "new",
        area: "Till",
        title: "Weigh at the till",
        body: "Tap a weighed product, type the weight from the scale (0.75 kg or 750 g) or tap 250 g / 500 g / 1 kg, and the price is worked out for you. Change the weight on the cart line any time.",
        where: "Till",
      },
      {
        kind: "new",
        area: "Till",
        title: "Scale labels scan straight in",
        body: "Give a weighed product a scale PLU. Labels printed by your shop scale (starting with 21 — PLU and weight in grams) add the item with its weight in one scan.",
        where: "Products → open a product → Inventory → Sold by weight",
      },
      {
        kind: "new",
        area: "Stock",
        title: "Receive by the kilo",
        body: "On Receive stock, weighed products take kg and cost per kg — 25.5 kg at GH₵18 adds exactly that to your stock and books.",
        link: "/receive-stock",
      },
    ],
  },
  {
    id: "2026-10-12-2",
    date: "12 October 2026",
    title: "Receive by the carton, costs to the pesewa",
    summary: "Record deliveries the way they arrive — 10 cartons at GH₵40 — and your stock value and profit stay exact.",
    audience: "owners",
    items: [
      {
        kind: "new",
        area: "Stock",
        title: "Receive stock in cartons",
        body: "On Receive stock, choose how each item arrived (single bottles or a Crate of 24), then type how many and what one cost. Eaze adds the singles to stock. Scanning a carton's barcode picks the carton for you.",
        where: "Sidebar → Receive stock",
        link: "/receive-stock",
      },
      {
        kind: "fixed",
        area: "Everywhere",
        title: "Exact cost of goods",
        body: "When a delivery's cost doesn't divide evenly (GH₵400 for 120 bottles), the cost of what you sell now adds up to exactly what you paid — no lost or extra pesewas in your profit or stock value.",
      },
    ],
  },
  {
    id: "2026-10-12",
    date: "12 October 2026",
    title: "Sell by the carton or the single",
    summary: "One product, many ways to sell it: a single bottle, a pack of 6 or a crate of 24 — all from the same stock.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Stock",
        title: "Packs and cartons",
        body: "Open a product and add packs under Inventory: give each a name (Carton, Crate, Pack), how many singles it holds, its own price and wholesale price, and its own barcode. Stock is still counted in singles.",
        where: "Products → open a product → Inventory → Packs and cartons",
        link: "/products",
      },
      {
        kind: "new",
        area: "Till",
        title: "Pack buttons and carton barcodes at the till",
        body: "Products with packs show a button for each pack under the tile. Scanning a carton's own barcode adds the carton. A crate of 24 takes 24 from stock, and the till won't sell more than you have across singles and packs.",
        where: "Till",
      },
      {
        kind: "improved",
        area: "Receipts",
        title: "Receipts show what was bought",
        body: "Receipts, Sales and Orders show \"2 × Crate of 24\" with the crate price. Returns still let you take back single bottles from a crate.",
      },
    ],
  },
  {
    id: "2026-10-11-3",
    date: "11 October 2026",
    title: "See what's behind your cash and mobile money",
    summary: "Tap Cash or Mobile money to see every sale, balance collected and refund that makes up the figure.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Till",
        title: "Cash and mobile money breakdown",
        body: "Tap the Cash or Mobile money card to see what's in it: money taken at sales, plus balances collected on older sales, minus refunds. Tap any line to open the sale.",
        where: "Sales history → Cash today · Orders → Cash",
      },
      {
        kind: "new",
        area: "Everywhere",
        title: "Cash and mobile money on the Orders page",
        body: "Owners now see the same Cash and Mobile money figures as cashiers, for any day or date range, with who holds what. Pick a cashier to see just their drawer.",
        where: "Orders",
        link: "/orders",
      },
    ],
  },
  {
    id: "2026-10-11-2",
    date: "11 October 2026",
    title: "Selling on credit, with a manager's OK",
    summary: "Set how much each customer can owe, and let a manager approve anything above it — at the till or from their phone.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Settings",
        title: "Credit limits",
        body: "Choose how much retail, wholesale and distributor customers can owe, and how many days they have to pay. You can also give a single customer their own limit.",
        where: "Settings → Business → Credit · Customers → open a customer",
        link: "/settings",
      },
      {
        kind: "new",
        area: "Till",
        title: "Part payments over the limit",
        body: "If a sale would take a customer over their limit, the till asks for a manager PIN. Manager not there? Tap \"Ask a manager to approve\" and the sale finishes by itself once they say yes.",
        where: "Till → Part payment (F11)",
      },
      {
        kind: "fixed",
        area: "Till",
        title: "Sales history updates straight away",
        body: "A sale you just finished now shows in Sales history at once, and cash and mobile money show how much came from balances collected on older sales.",
      },
    ],
  },
  {
    id: "2026-10-11",
    date: "11 October 2026",
    title: "Collect balances anywhere, and print shelf labels",
    summary: "Customers who pay in parts can settle at any till, and every product can have a price label that scans.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Till",
        title: "Collect a balance at the till",
        body: "When a customer comes back to pay what they owe, tap Collect balance, find the sale by its receipt number, and take cash or mobile money. The sale completes when it's fully paid.",
        where: "Till → Collect balance · Sales → open a sale → Collect",
      },
      {
        kind: "fixed",
        area: "Till",
        title: "Part payments count on the day they're paid",
        body: "Money collected later now shows on the day, shift and drawer it was received in — not on the day of the original sale — so cash counts tally.",
      },
      {
        kind: "fixed",
        area: "Everywhere",
        title: "Profit is right for part-paid sales",
        body: "The cost of goods on a part-paid sale is now booked when the goods leave the shop, not when the last payment arrives.",
      },
      {
        kind: "new",
        area: "Stock",
        title: "Shelf labels with barcodes",
        body: "Print price labels on A4 sticker sheets or a label printer. Products without a barcode can get a store barcode in one tap, so every label scans at the till.",
        where: "Products → Print labels",
        link: "/labels",
      },
      {
        kind: "new",
        area: "Till",
        title: "My returns for cashiers",
        body: "Cashiers can see every return they sent for approval — waiting, approved or declined, with the manager's note — and the returns they've done.",
        where: "Cashier menu → My returns",
      },
    ],
  },
  {
    id: "2026-10-06-2",
    date: "6 October 2026",
    title: "Returns and refunds",
    summary: "Take items back properly: the customer gets the right amount, stock goes back on the shelf, and your books stay correct.",
    audience: "owners",
    items: [
      {
        kind: "new",
        area: "Receipts",
        title: "Return items from a sale",
        body: "Open a sale in Orders and choose Return items. Pick how many of each item, why it's coming back, and refund by cash, mobile money or bank. Print a return slip for the customer.",
        where: "Orders → open a sale → Return items",
        link: "/orders",
      },
      {
        kind: "new",
        area: "Till",
        title: "Returns at the till, approved by a manager",
        body: "Cashiers press Return (F7), find the sale by its receipt number, and a manager types their PIN to approve. Managers set their PIN in Settings → Profile. You choose the rule in Settings → Business.",
        where: "Till → Return (F7)",
        link: "/settings",
      },
      {
        kind: "new",
        area: "Till",
        title: "Approve returns from anywhere",
        body: "Manager not in the shop? The cashier taps \"Ask a manager to approve\". Managers get a pop-up in Eaze and an email, approve with their PIN from their phone, and the till updates by itself.",
        where: "Till → Return → Ask a manager to approve",
      },
      {
        kind: "improved",
        area: "Till",
        title: "Sales totals subtract returns",
        body: "Sales history, the Orders page and the dashboard now show sales after returns, and cash and mobile money after refunds — so the numbers match what's in the drawer.",
      },
      {
        kind: "new",
        area: "Stock",
        title: "Returns page",
        body: "See every return in one place: how much went back, how much was refunded, what was written off, and why things come back.",
        where: "Sidebar → Returns",
        link: "/returns",
      },
      {
        kind: "new",
        area: "Stock",
        title: "Back on the shelf, or written off",
        body: "Good items go back into stock at what they cost you. Damaged or expired items are written off, so your losses show up clearly in your reports.",
      },
      {
        kind: "improved",
        area: "Till",
        title: "Fair refunds, every time",
        body: "Eaze works out the refund from what the customer actually paid, including any discount. If they still owe on the sale, the return comes off what they owe first.",
      },
      {
        kind: "fixed",
        area: "Till",
        title: "Shift reports count correctly",
        body: "Cancelled sales no longer count as refunds, returns appear on the shift they were made in, and cash refunds come out of the expected cash in the drawer.",
      },
    ],
  },
  {
    id: "2026-10-06",
    date: "6 October 2026",
    title: "Faster checkout, barcodes and clearer receipts",
    summary:
      "Built for busy counters: scan, key in and finish a sale without touching the mouse, and receive deliveries in one screen.",
    audience: "everyone",
    items: [
      {
        kind: "new",
        area: "Till",
        title: "Barcode scanning at the till",
        body: "Plug in any USB or Bluetooth scanner and scan — the item goes straight into the sale. No scanner? Type the barcode in the search box and press Enter.",
        where: "Till · Products → Barcodes",
        link: "/products",
      },
      {
        kind: "new",
        area: "Till",
        title: "Keyboard checkout",
        body: "Run a whole sale from the keyboard: F9 cash, F10 mobile money, F12 finish, + and − for quantities. Press ? at the till to see every key, or practise them in the Help centre.",
        where: "Till · Help → Keyboard shortcuts",
        link: "/help/keyboard",
      },
      {
        kind: "new",
        area: "Stock",
        title: "Receive stock",
        body: "Record a supplier delivery or market run in one screen: scan items, enter costs, choose how you paid. Stock and your books update together.",
        where: "Sidebar → Receive stock",
        link: "/receive-stock",
      },
      {
        kind: "improved",
        area: "Receipts",
        title: "Receipts show every price",
        body: "Receipts now show each item's price and line total, the discount, the total, the cash received, the change, and any balance still owed.",
      },
      {
        kind: "improved",
        area: "Everywhere",
        title: "Clearer messages",
        body: "When something can't be done, Eaze now tells you why in plain words — and what to do next.",
      },
    ],
  },
  {
    id: "2026-10-03",
    date: "3 October 2026",
    title: "Simpler plans, a free trial and more control over billing",
    summary: "Know exactly what you're paying for, what you're using, and when your plan ends.",
    audience: "owners",
    items: [
      {
        kind: "new",
        area: "Billing",
        title: "7-day free trial",
        body: "New businesses can try Eaze free for 7 days — no card needed. Pick a plan whenever you're ready.",
        link: "/subscriptions",
      },
      {
        kind: "new",
        area: "Billing",
        title: "Plan usage",
        body: "See how many users, branches and businesses you're using against what your plan includes.",
        where: "Subscriptions → Plan usage",
        link: "/subscriptions",
      },
      {
        kind: "improved",
        area: "Billing",
        title: "No surprise charges",
        body: "Plans don't charge automatically. We email you 10, 7, 5 and 3 days before your plan ends, and you renew when you choose — card, mobile money or bank.",
        link: "/subscriptions",
      },
      {
        kind: "improved",
        area: "Billing",
        title: "Cancel in a few clicks",
        body: "Cancel from Subscriptions and tell us why if you like. You keep access until the end of the period you paid for.",
      },
      {
        kind: "new",
        area: "Settings",
        title: "VAT on or off",
        body: "Only VAT-registered businesses should charge VAT. Switch it on in Settings → Business; when it's off, sales count in full as your revenue.",
        link: "/settings",
      },
      {
        kind: "fixed",
        area: "Billing",
        title: "Correct renewal dates",
        body: "Plan end dates now always land on the right day of the month, including for plans started on the 29th, 30th or 31st.",
      },
    ],
  },
];

export const LATEST_RELEASE_ID = RELEASES[0]?.id ?? "";
export const SEEN_RELEASE_KEY = "eaze:lastSeenRelease";

export function hasUnseenRelease(): boolean {
  try {
    return (localStorage.getItem(SEEN_RELEASE_KEY) || "") < LATEST_RELEASE_ID;
  } catch {
    return false;
  }
}

export function markReleasesSeen(): void {
  try {
    localStorage.setItem(SEEN_RELEASE_KEY, LATEST_RELEASE_ID);
    window.dispatchEvent(new Event("eaze:releases-seen"));
  } catch {
    /* storage unavailable — fine */
  }
}
